import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/providers/auth-provider';
import { meApi } from '@/services/api';

export const Route = createFileRoute('/pending')({
  component: PendingPage,
});

function PendingPage() {
  const { status, session, refresh } = useAuth();
  const navigate = useNavigate();
  const [note, setNote] = useState('');

  const user = session?.user;
  const isApproved = user?.isAdmin || user?.status === 'approved';

  // Send already-resolved users where they belong; only pending/rejected stay here.
  useEffect(() => {
    if (status === 'unauthenticated') {
      navigate({ to: '/login' });
    } else if (status === 'authenticated' && isApproved) {
      navigate({ to: '/' });
    }
  }, [status, isApproved, navigate]);

  const submitNote = useMutation({
    mutationFn: (text: string) => meApi.submitNote(text),
    onSuccess: () => refresh(),
  });

  if (status === 'loading' || !user || isApproved) {
    return null;
  }

  if (user.status === 'rejected') {
    return (
      <Shell title="Not this time, hero" description="Thanks for your interest in a mission to see Chris.">
        <div className="comic-bubble p-4">
          <p className="text-sm text-muted-foreground">
            Looks like the Joker blew up your access. If you think this is a mistake, please reach out to the family
            directly.
          </p>
        </div>
      </Shell>
    );
  }

  // Pending and they've already told us how they know Chris → just wait.
  if (user.note) {
    return (
      <Shell title="You're in the line-up!" description="Chris's side kicks will review your request soon.">
        <div className="comic-bubble p-4">
          <p className="font-medium">What you told us:</p>
          <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{user.note}</p>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          We'll fire the Bat-Signal when you're cleared. You can close this page — nothing else to do for now.
        </p>
      </Shell>
    );
  }

  // Pending, no note yet → ask how they know Chris.
  return (
    <Shell
      title="Almost a hero!"
      description="Chris is recovering and can only have a few heroes a day, so his side kicks clear everyone before granting access to the site."
    >
      <div className="comic-bubble mb-4 p-4">
        <p className="text-sm text-muted-foreground">
          One quick origin story and you're on the team — tell us how you know Chris below.
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (note.trim()) {
            submitNote.mutate(note.trim());
          }
        }}
        className="space-y-3"
      >
        <label htmlFor="note" className="text-sm font-medium">
          How do you know Chris?
        </label>
        <p className="text-xs text-muted-foreground">
          In case we don't recognise your email address — a quick note helps us know who you are.
        </p>
        <Textarea
          id="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. I'm Chris's neighbour from across the road"
          rows={4}
          required
        />
        {submitNote.isError && (
          <p className="text-sm text-destructive">Something went wrong sending that. Please try again.</p>
        )}
        <Button type="submit" disabled={!note.trim() || submitNote.isPending} className="w-full">
          {submitNote.isPending ? 'Sending…' : 'Request a mission'}
        </Button>
      </form>
    </Shell>
  );
}

function Shell({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-3xl tracking-wide text-primary">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}
